import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, Mail, User, ArrowLeft, Shield } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const response = await api.post(endpoint, formData);
      
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin_data', JSON.stringify(response.data.admin));
      
      toast.success(isRegister ? 'Account created successfully!' : 'Welcome back!');
      navigate('/admin/dashboard');
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Authentication failed. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center px-6 relative overflow-hidden" data-testid="admin-login-page">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-rose-gold/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-rose-gold/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-4xl font-bold text-rose-gold">PROPVANTA</h1>
            <p className="text-white/40 text-xs uppercase tracking-widest mt-2">Property Consultants</p>
          </Link>
        </div>

        {/* Login Card */}
        <div className="glass-effect border border-dark-lighter rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-gold/10 rounded-full mb-4">
              <Shield className="text-rose-gold" size={32} />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-white">
              {isRegister ? 'Create Admin Account' : 'Admin Portal'}
            </h2>
            <p className="text-white/60 text-sm mt-2">
              {isRegister ? 'Set up your admin credentials' : 'Sign in to manage your properties'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                  Full Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                  <Input
                    id="name"
                    name="name"
                    data-testid="admin-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required={isRegister}
                    className="bg-dark border-dark-lighter text-white pl-10 focus:border-rose-gold"
                    placeholder="John Doe"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="admin-email-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-dark border-dark-lighter text-white pl-10 focus:border-rose-gold"
                  placeholder="admin@propvanta.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="admin-password-input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="bg-dark border-dark-lighter text-white pl-10 focus:border-rose-gold"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="admin-submit-button"
              disabled={loading}
              className="w-full btn-primary py-6 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {isRegister ? 'Creating Account...' : 'Signing In...'}
                </>
              ) : isRegister ? (
                'Create Account'
              ) : (
                'Sign In'
              )}
            </Button>

            <div className="text-center pt-4 border-t border-dark-lighter">
              <button
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-white/60 text-sm hover:text-rose-gold transition-colors"
              >
                {isRegister
                  ? 'Already have an account? Sign in'
                  : "Don't have an account? Create one"}
              </button>
            </div>
          </form>
        </div>

        {/* Back to Website */}
        <div className="text-center mt-6">
          <Link 
            to="/" 
            className="text-white/40 text-sm hover:text-white transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to Website
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
